/**
 * useConversationalCoach Hook
 *
 * Manages conversational discovery coach state and interactions.
 * Replaces the step-based useCoachWorkflow with natural conversation flow.
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { useAIStore } from '@/stores/aiStore'
import {
  discoveryApi,
  type ConversationMode,
  type TopicCoverage,
  type TopicCoverageResponse,
  type ConversationalChatEvent,
  type TagSelection,
} from '@/lib/api/client'

// ============================================================================
// TYPES
// ============================================================================

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: Date
  isStreaming?: boolean
  quickReplies?: string[]
}

export interface ConversationalCoachState {
  messages: ChatMessage[]
  topicCoverage: TopicCoverageResponse | null
  isLoading: boolean
  isStreaming: boolean
  error: string | null
  mode: ConversationMode
  quickReplies: string[]
  tagSelection: TagSelection | null
}

export interface UseConversationalCoachReturn {
  // State
  messages: ChatMessage[]
  topicCoverage: TopicCoverageResponse | null
  isLoading: boolean
  isStreaming: boolean
  error: string | null
  mode: ConversationMode
  quickReplies: string[]
  tagSelection: TagSelection | null

  // Actions
  sendMessage: (content: string) => Promise<void>
  selectQuickReply: (reply: string) => void
  selectTags: (selectedIds: string[], otherText?: string) => void
  skipTagSelection: () => void
  setMode: (mode: ConversationMode) => void
  clearMessages: () => void
  loadCoverage: () => Promise<void>
  initializeCoverage: () => Promise<void>

  // Config
  isConfigured: boolean
}

// ============================================================================
// UTILITIES
// ============================================================================

/**
 * Strip the ```json:meta ... ``` block from streamed content for display.
 * The metadata is parsed separately after streaming completes.
 */
function stripMetadataBlock(content: string): string {
  // Remove complete metadata blocks
  let cleaned = content.replace(/```json:meta[\s\S]*?```/g, '')
  // Also remove partial/incomplete metadata blocks at the end (during streaming)
  cleaned = cleaned.replace(/```json:meta[\s\S]*$/g, '')
  return cleaned.trim()
}

// ============================================================================
// HOOK
// ============================================================================

export function useConversationalCoach(
  accountId: string,
  projectId: string,
  sessionId: string | undefined
): UseConversationalCoachReturn {
  const { apiKey, coachingModel, conversationMode } = useAIStore()

  // State
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [topicCoverage, setTopicCoverage] = useState<TopicCoverageResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mode, setMode] = useState<ConversationMode>(conversationMode || 'organic')
  const [quickReplies, setQuickReplies] = useState<string[]>([])
  const [tagSelection, setTagSelection] = useState<TagSelection | null>(null)

  // Refs
  const abortControllerRef = useRef<AbortController | null>(null)

  // Check if configured
  const isConfigured = Boolean(apiKey && sessionId)

  // Load topic coverage
  const loadCoverage = useCallback(async () => {
    if (!sessionId) return

    try {
      const coverage = await discoveryApi.getTopicCoverage(accountId, projectId, sessionId, mode)
      setTopicCoverage(coverage)
    } catch (err) {
      console.error('Failed to load topic coverage:', err)
    }
  }, [accountId, projectId, sessionId, mode])

  // Load messages from database
  const loadMessages = useCallback(async () => {
    if (!sessionId) return

    try {
      const apiMessages = await discoveryApi.getMessages(accountId, projectId, sessionId)
      const chatMessages: ChatMessage[] = apiMessages.map((m) => ({
        id: m.id,
        role: m.role,
        content: m.content,
        timestamp: new Date(m.timestamp),
        quickReplies: (m.metadata?.quickReplies as string[] | undefined) || undefined,
      }))
      setMessages(chatMessages)
    } catch (err) {
      console.error('Failed to load messages:', err)
    }
  }, [accountId, projectId, sessionId])

  // Initialize coverage for new session
  const initializeCoverage = useCallback(async () => {
    if (!sessionId) return

    try {
      await discoveryApi.initializeTopicCoverage(accountId, projectId, sessionId)
      await loadCoverage()
    } catch (err) {
      console.error('Failed to initialize topic coverage:', err)
    }
  }, [accountId, projectId, sessionId, loadCoverage])

  // Load messages and coverage on session change
  useEffect(() => {
    if (sessionId) {
      loadMessages()
      loadCoverage()
    } else {
      // Clear messages when no session
      setMessages([])
    }
  }, [sessionId, loadMessages, loadCoverage])

  // Reload coverage on mode change
  useEffect(() => {
    if (sessionId) {
      loadCoverage()
    }
  }, [mode, loadCoverage])

  // Send a message
  const sendMessage = useCallback(
    async (content: string) => {
      if (!sessionId || !apiKey || !content.trim()) return

      setError(null)
      setIsLoading(true)
      setQuickReplies([]) // Clear previous quick replies
      setTagSelection(null) // Clear previous tag selection

      // Add user message
      const userMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'user',
        content: content.trim(),
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, userMessage])

      // Add placeholder assistant message for streaming
      const assistantMessageId = crypto.randomUUID()
      const assistantMessage: ChatMessage = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        isStreaming: true,
      }
      setMessages((prev) => [...prev, assistantMessage])

      setIsStreaming(true)

      try {
        // Use streaming API
        let streamedContent = ''
        let finalQuickReplies: string[] = []
        let finalTagSelection: TagSelection | null = null

        await discoveryApi.chatStream(
          accountId,
          projectId,
          sessionId,
          {
            message: content.trim(),
            mode,
            apiKey,
            model: coachingModel,
          },
          (event: ConversationalChatEvent) => {
            switch (event.type) {
              case 'text':
                if (event.content) {
                  streamedContent += event.content
                  // Strip metadata block for display (it's parsed after streaming)
                  const displayContent = stripMetadataBlock(streamedContent)
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === assistantMessageId
                        ? { ...m, content: displayContent }
                        : m
                    )
                  )
                }
                break

              case 'quick_replies':
                if (event.replies) {
                  finalQuickReplies = event.replies
                }
                break

              case 'tag_selection':
                if (event.tagSelection) {
                  finalTagSelection = event.tagSelection
                }
                break

              case 'topic_update':
                // Coverage will be updated in the done event
                break

              case 'entity_extracted':
                // Entities are tracked in coverage
                break

              case 'done':
                // Update coverage from final state
                if (event.topicCoverage) {
                  loadCoverage()
                }
                break

              case 'error':
                setError(event.message || 'Unknown error')
                break
            }
          }
        )

        // Finalize message with cleaned content
        const finalContent = stripMetadataBlock(streamedContent)
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMessageId
              ? {
                  ...m,
                  content: finalContent,
                  isStreaming: false,
                  quickReplies: finalQuickReplies.length > 0 ? finalQuickReplies : undefined,
                }
              : m
          )
        )

        // Set quick replies for display
        if (finalQuickReplies.length > 0) {
          setQuickReplies(finalQuickReplies)
        }

        // Set tag selection for display
        if (finalTagSelection) {
          setTagSelection(finalTagSelection)
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to send message'
        setError(errorMessage)

        // Update assistant message to show error
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMessageId
              ? {
                  ...m,
                  content: `Sorry, something went wrong: ${errorMessage}`,
                  isStreaming: false,
                }
              : m
          )
        )
      } finally {
        setIsLoading(false)
        setIsStreaming(false)
      }
    },
    [sessionId, apiKey, accountId, projectId, mode, coachingModel, loadCoverage]
  )

  // Select a quick reply
  const selectQuickReply = useCallback(
    (reply: string) => {
      setQuickReplies([]) // Clear quick replies immediately
      sendMessage(reply)
    },
    [sendMessage]
  )

  // Select tags from tag selection
  const selectTags = useCallback(
    (selectedIds: string[], otherText?: string) => {
      if (!tagSelection) return

      // Build message from selected tags
      const selectedLabels = selectedIds
        .map((id) => tagSelection.options.find((opt) => opt.id === id)?.label)
        .filter(Boolean)

      let message = ''
      if (selectedLabels.length > 0) {
        message = selectedLabels.join(', ')
      }
      if (otherText) {
        message = message ? `${message}, ${otherText}` : otherText
      }

      setTagSelection(null) // Clear tag selection
      if (message) {
        sendMessage(message)
      }
    },
    [tagSelection, sendMessage]
  )

  // Skip tag selection
  const skipTagSelection = useCallback(() => {
    setTagSelection(null)
  }, [])

  // Clear messages
  const clearMessages = useCallback(() => {
    setMessages([])
    setQuickReplies([])
    setTagSelection(null)
    setError(null)
  }, [])

  return {
    // State
    messages,
    topicCoverage,
    isLoading,
    isStreaming,
    error,
    mode,
    quickReplies,
    tagSelection,

    // Actions
    sendMessage,
    selectQuickReply,
    selectTags,
    skipTagSelection,
    setMode,
    clearMessages,
    loadCoverage,
    initializeCoverage,

    // Config
    isConfigured,
  }
}

export type { ConversationMode, TopicCoverage, TopicCoverageResponse, TagSelection }
